import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { Tenant } from '@shared/definitions/tenant';
import { Contact } from '@shared/definitions/contact';
import { TenantsService } from '@core/services/data/tenants.service';

/**
 * Phase 4 — signal-based store for the tenant list + currently-viewed
 * tenant. Mirrors PropertyStore / ContractStore / WorkspaceStore / AuthStore.
 *
 * Service layer (`TenantsService`) owns Firestore reads/writes and mirrors
 * its `entitiesSubject` into this store via a bridge. Consumers read
 * signals here instead of subscribing to the service observable.
 *
 * Write actions delegate to TenantsService via a lazy Injector lookup
 * (same pattern as LoanStore) — avoids the circular DI that would happen
 * if the store injected the service directly while the service injects the
 * store for the read-path bridge.
 */
@Injectable({ providedIn: 'root' })
export class TenantStore {
  private readonly _list = signal<readonly Tenant[]>([]);
  private readonly _currentId = signal<string | null>(null);
  private readonly _loading = signal(false);

  /**
   * UIDs upserted locally but not yet confirmed by a Firestore snapshot
   * (#541). `setList()` mirrors every emission from `TenantsService` as a
   * wholesale replace — correct once the snapshot is caught up, wrong for
   * the few hundred ms right after an optimistic `upsert()`, where a
   * snapshot taken before the new doc is indexed server-side would silently
   * erase it again. Each entry expires on its own: if the real snapshot
   * genuinely never includes it (the write failed, was reverted…), this
   * must stop pretending otherwise rather than pin a ghost tenant forever.
   */
  private readonly _pendingUpserts = new Map<string, { tenant: Tenant; expiresAt: number }>();
  private static readonly PENDING_UPSERT_TTL_MS = 30_000;

  private readonly injector = inject(Injector);
  private _service: TenantsService | null = null;
  private get service(): TenantsService {
    return (this._service ??= this.injector.get(TenantsService));
  }

  readonly list = this._list.asReadonly();
  readonly currentId = this._currentId.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly count = computed(() => this._list().length);

  readonly current = computed(() => {
    const id = this._currentId();
    if (!id) return null;
    return this._list().find(t => t.tenantUID === id) ?? null;
  });

  /** Tenants filtered by a property assignment (if any). */
  readonly byPropertyId = (propertyUID: string) =>
    computed(() => this._list().filter(t => {
      const properties = (t as Tenant & { properties?: { propertyUID: string }[] }).properties;
      return properties?.some(p => p.propertyUID === propertyUID) ?? false;
    }));

  /** Synchronous lookup. */
  getById(tenantUID: string): Tenant | null {
    return this._list().find(t => t.tenantUID === tenantUID) ?? null;
  }

  setList(tenants: readonly Tenant[]): void {
    if (this._pendingUpserts.size === 0) {
      this._list.set(tenants);
      return;
    }

    const now = Date.now();
    const present = new Set(tenants.map(t => t.tenantUID));
    const merged = tenants.slice();
    for (const [uid, entry] of this._pendingUpserts) {
      if (entry.expiresAt <= now || present.has(uid)) {
        // Confirmed by the snapshot, or gave up waiting — either way, done.
        this._pendingUpserts.delete(uid);
        continue;
      }
      // Not in this snapshot yet: keep it, rather than let a wholesale
      // replace make it disappear mid-wizard (#541).
      merged.push(entry.tenant);
    }
    this._list.set(merged);
  }

  upsert(tenant: Tenant): void {
    const list = this._list();
    const i = list.findIndex(t => t.tenantUID === tenant.tenantUID);
    if (i === -1) {
      this._list.set([...list, tenant]);
    } else {
      const next = list.slice();
      next[i] = tenant;
      this._list.set(next);
    }
    this._pendingUpserts.set(tenant.tenantUID, {
      tenant,
      expiresAt: Date.now() + TenantStore.PENDING_UPSERT_TTL_MS,
    });
  }

  remove(tenantId: string): void {
    this._list.set(this._list().filter(t => t.tenantUID !== tenantId));
    if (this._currentId() === tenantId) {
      this._currentId.set(null);
    }
  }

  setCurrentId(id: string | null): void {
    this._currentId.set(id);
  }

  setLoading(value: boolean): void {
    this._loading.set(value);
  }

  reset(): void {
    this._list.set([]);
    this._currentId.set(null);
    this._loading.set(false);
  }

  /** Create a tenant. Returns the new tenantUID. */
  addTenant(tenant: Omit<Tenant, 'tenantUID'>): Observable<string> {
    return this.service.addTenant(tenant);
  }

  /** Patch an existing tenant. */
  updateTenant(tenantUID: string, updates: Partial<Tenant>): Observable<void> {
    return this.service.updateTenant(tenantUID, updates);
  }

  /** Delete a tenant. */
  deleteTenant(tenantUID: string): Observable<void> {
    return this.service.deleteTenant(tenantUID);
  }

  /** Soft-archive a tenant (sets active=false). */
  archiveTenant(tenantUID: string): Observable<void> {
    return this.service.archiveTenant(tenantUID);
  }

  /** Append a contact to a tenant's contacts array. */
  addContactToTenant(contact: Contact, tenantUID: string): Observable<void> {
    return this.service.addContactToTenant(contact, tenantUID);
  }

  /** Remove a contact from a tenant's contacts array. */
  removeContactFromTenant(contact: Contact, tenantUID: string): Observable<void> {
    return this.service.removeContactFromTenant(contact, tenantUID);
  }

  /** Replace one contact in a tenant with an updated version. */
  updateContactInTenant(updated: Contact, original: Contact, tenantUID: string): Observable<void> {
    return this.service.updateContactInTenant(updated, original, tenantUID);
  }

  /** Synchronous display-name helper. */
  getTenantName(tenant: Tenant): string {
    return this.service.getTenantName(tenant);
  }

  /** Synchronous name lookup across a list of tenant UIDs. */
  getTenantNameByUIDs(tenantUIDs: (string | undefined)[]): string {
    return this.service.getTenantNameByUIDs(tenantUIDs);
  }
}
